import React from "react"
import PropTypes from "prop-types"
import ReactHtmlParser, { convertNodeToElement } from "react-html-parser"
import Accordion from "@components/blog/accordion"
import ExampleImage from "@components/dashboard-examples/example-image"
import Paragraph from "@elements/paragraph"
import Heading from "@elements/heading"
import Anchor from "@elements/anchor"
import Quote from "@elements/quote"
import List from "@elements/list"
import Image from "@components/image"
import Div from "@elements/div"
import theme from "@styles/theme"
import Grid from "@elements/grid"
import Flex from "@elements/flex"
import { removeKeys } from "@utils/remove-key"
import YouTubeEmbed from "./directus/youtube-embed"
import SignUpCTA from "./cta/sign-up-cta"
import ImageWrapper from "./directus/image-wrapper"
import { WideColourfulCTA } from "./cta/colourful/wide"
import { HtmlStylesType } from "@types/html-styles"

const findImage = (images, assetId) => {
  if (!images) return null
  const image = images.find(image => image.directus_id === assetId)
  if (image) {
    return image
  } else {
    return null
  }
}

const parseDirectusImage = (node, images, index, shadow) => {
  const src = node.attribs.src || node.attribs["data-src"]
  const UrlAssetIdREgex = /(\/assets\/)([\da-z-]*)/m
  const assetId = src.match(UrlAssetIdREgex)?.[2]

  // let url;

  // try {
  //   url = new URL(src);
  // } catch (_) {
  //   return null;
  // }

  // const params = url.searchParams;

  if (assetId) {
    const image = findImage(images, assetId)
    if (!image) return null
    return shadow ? (
      <Image
        key={`image-${index}`}
        className={node.attribs.class}
        file={image}
        radius={0.5}
        alt={image.title}
        showOverflow
        borderStyle={{
          boxShadow: theme.shadow.dramatic,
        }}
      />
    ) : (
      <Image
        key={`image-${index}`}
        className={node.attribs.class}
        file={image}
        radius={0.5}
        alt={image.title}
      />
    )
  }
}

const parseImage = (node, images, index, shadow) => {
  const src = node.attribs.src || node.attribs["data-src"]
  const UrlAssetIdREgex = /(\/assets\/)([\da-z-]*)/m
  const assetId = src.match(UrlAssetIdREgex)?.[2]

  // let url;

  // try {
  //   url = new URL(src);
  // } catch (_) {
  //   return null;
  // }

  // const params = url.searchParams;

  if (assetId) {
    const image = findImage(images, assetId)
    if (!image) return null
    return shadow ? (
      <Image
        key={`image-${index}`}
        className={node.attribs.class}
        file={image}
        radius={0.5}
        alt={image.title}
        showOverflow
        borderStyle={{
          boxShadow: theme.shadow.dramatic,
        }}
      />
    ) : (
      <Image
        key={`image-${index}`}
        className={node.attribs.class}
        file={image}
        radius={0.5}
        alt={image.title}
        style={{ marginBottom: "30px" }}
      />
    )
  }
}

const getPanelChildren = node => {
  let nodes = []
  for (let child in node.children) {
    const childNode = node.children[child]
    if (childNode.type === "tag" && childNode.name === "div") {
      for (let p in childNode.children) {
        const pNode = childNode.children[p]
        if (pNode.type === "tag" && pNode.name === "p") {
          nodes.push(pNode)
        }
      }
    }
  }
  return nodes
}

const getPanels = node => {
  if (!node || !node.next) return null
  if (node.type !== "tag") return getPanels(node.next)
  if (node.name !== "div" || !node.attribs.class.includes("accordion-panel"))
    return getPanels(node.next)

  return getPanelChildren(node)
}

const parseAccordion = (node, index, transform) => {
  const panels = getPanels(node.next)

  if (!panels) return null

  const elements = panels.map((panel, panelIndex) => {
    return convertNodeToElement(panel, panelIndex, transform)
  })
  const title = node.children.find(child => child.type === "text").data
  return (
    <Accordion key={`accordion-${index}`} title={title}>
      {elements}
    </Accordion>
  )
}

const parseExampleGrid = (node, index, transform, images) => {
  const nodes = parseExampleChildren(node, index, transform, images)
  return (
    <Grid
      columns="1fr 1fr 1fr"
      columnsMd="1fr 1fr"
      columnsSm="1fr"
      gap="2.5rem"
      margin="0 0 2rem"
      marginMd="0 0 2rem"
      key={`example-grid-${index}`}
    >
      {nodes}
    </Grid>
  )
}

const parseExampleCardContainer = (node, index, transform, images) => {
  const nodes = parseExampleChildren(node, index, transform, images)
  return (
    <Flex key={`example-card-container-${index}`} height="100%">
      {nodes}
    </Flex>
  )
}

const parseExampleCardImage = (node, index, transform, images) => {
  return (
    <Flex
      padding="1.5rem"
      background="#f3f5fc"
      alignItems="center"
      justifyContent="center"
      borderRadius="0.5rem"
      height="220px"
      key={`example-card-image-${index}`}
    >
      {parseImage(node.children[0], images, index, true)}
    </Flex>
  )
}

const parseExampleCardBody = (node, index, transform) => {
  const nodes = parseNodeChildren(node, index, transform, null, true)
  return (
    <Flex
      justifyContent="flex-start"
      fontSize="1.1rem"
      gap="1rem"
      margin="2rem 0 1rem"
      lineHeight="1.7"
      key={`example-card-body-${index}`}
    >
      {nodes}
    </Flex>
  )
}

const parseExampleCardBottom = (node, index, transform) => {
  const nodes = parseNodeChildren(node, index, transform, null, true)
  return (
    <Div fontSize="1.1rem" key={`example-card-bottom-${index}`}>
      {nodes}
    </Div>
  )
}

const parseExampleContainer = (node, index, transform, images) => {
  const nodes = parseExampleChildren(node, index, transform, images)
  return nodes
}

const parseExampleCard = (node, index, transform, images) => {
  switch (node.attribs?.class) {
    case "card":
      return parseExampleCardContainer(node, index, transform, images)
    case "card-img":
      return parseExampleCardImage(node, index, transform, images)
    case "card-body":
      return parseExampleCardBody(node, index, transform)
    case "card-bottom":
      return parseExampleCardBottom(node, index, transform)
    case "example-container":
      return parseExampleContainer(node, index, transform, images)
  }

  return convertNodeToElement(node, index, transform)
}

const parseExampleChildren = (node, index, transform, images) => {
  let nodes = []
  for (let child in node.children) {
    const childNode = node.children[child]
    if (childNode.children) {
      const newNode = parseExampleCard(childNode, index, transform, images)
      nodes.push(newNode)
    } else {
      nodes.push(childNode.data)
    }
  }
  return nodes
}

const parseQuote = (node, index, transform) => {
  const nodes = parseNodeChildren(node, index, transform, null, false)
  return (
    <Quote key={`quote-${index}`}>
      {nodes.map(node => {
        return node
      })}
    </Quote>
  )
}

const parseParagraph = (node, index, transform, styles, inherit) => {
  const nodes = parseNodeChildren(node, index, transform, styles, inherit)
  return (
    <Paragraph
      inherit={inherit}
      fontWeight={styles?.paragraph?.fontWeight ?? "inherit"}
      fontSize={styles?.paragraph?.fontSize}
      color={styles?.paragraph?.color}
      lineHeight={styles?.paragraph?.lineHeight}
      key={`paragraph-${index}`}
    >
      {nodes.map((node, i) => {
        return (
          <React.Fragment key={`paragraph-item-${i}`}>{node}</React.Fragment>
        )
      })}
    </Paragraph>
  )
}

const parseList = (node, index, transform, styles, inherit) => {
  const nodes = parseNodeChildren(node, index, transform, styles, inherit)
  return (
    <List color={styles?.list?.color} lineHeight={styles?.list?.lineHeight} inherit={inherit} key={`list-${index}`} as={node.name}>
      {nodes.map((node, i) => {
        return <React.Fragment key={`list-item-${i}`}>{node}</React.Fragment>
      })}
    </List>
  )
}

const parseHeading = (node, index, transform, styles) => {
  const nodes = parseNodeChildren(node, index, transform, styles, false)
  const attribs = removeKeys(node.attribs, ["class", "style"])
  return (
    <Heading color={styles?.heading?.color} key={`heading-${index}`} as={node.name} {...attribs}>
      {nodes.map((node, i) => {
        return <React.Fragment key={`heading-item-${i}`}>{node}</React.Fragment>
      })}
    </Heading>
  )
}

// TODO FIX URL
// TODO TARGET AND REL
const parseAnchor = (node, index, transform, styles) => {
  const href = node.attribs?.href
  const nodes = parseNodeChildren(node, index, transform, styles, false)
  return (
    <Anchor
      color={styles?.link?.color}
      link={href}
      key={`anchor-${href?.slice(-5)}-${index}`}
      fontSize="inherit"
    >
      {nodes.map((node, i) => {
        return <React.Fragment key={`anchor-item-${i}`}>{node}</React.Fragment>
      })}
    </Anchor>
  )
}

const parseNodeChildren = (node, index, transform, styles, inherit) => {
  let nodes = []
  for (let child in node.children) {
    const childNode = node.children[child]
    if (childNode.children) {
      const newNode = parseElement(
        childNode,
        index,
        transform,
        true,
        null,
        styles,
        inherit
      )
      nodes.push(newNode)
    } else {
      nodes.push(childNode.data)
    }
  }
  return nodes
}

const parseExampleImage = (node, index, images) => {
  const exampleImages = node.children

  const elements = exampleImages.map((image, imageIndex) => {
    const isImageTag = image.type === "tag" && image.name === "img"
    if (isImageTag) {
      return parseImage(image, images, imageIndex, false)
    }
    return undefined
  })

  return <ExampleImage key={`example-grid-${index}`}>{elements}</ExampleImage>
}

const parseImageWrapper = (node, index, images, shadow) => {
  const exampleImages = node.children

  const elements = exampleImages.map((image, imageIndex) => {
    const isImageTag = image.type === "tag" && image.name === "img"
    if (isImageTag) {
      return parseDirectusImage(image, images, imageIndex, false)
    }
    return undefined
  })

  return (
    <ImageWrapper shadow={shadow} key={`image-wrapper-${index}`}>
      {elements}
    </ImageWrapper>
  )
}

const parseYouTubeEmbed = (node, index, transform, styles) => {
  const nodes = parseNodeChildren(node, index, transform, null, true)
  return (
    nodes[0] && <YouTubeEmbed key={`youtube-embed-${index}`} id={nodes[0]} poster={styles?.youtube?.poster} />
  )
}

const parseArticleCTA = index => {
  return (
    <Div key={`article-cta-${index}`} margin="0 0 2rem" hideOnSm>
      <WideColourfulCTA />
    </Div>
  )
}

const parseSignUpCTA = (node, index, transform) => {
  const nodes = parseNodeChildren(node, index, transform, null, true)
  return (
    nodes[0] && (
      <Div key={`signup-cta-${index}`} margin="0 0 2rem" maxWidth="450px">
        <SignUpCTA category={nodes[0]} />
      </Div>
    )
  )
}

const getCtaCount = html => {
  const wordCount = html.split(/\s+/).length
  let numCTAs
  if (wordCount < 500) {
    numCTAs = 0
  } else if (wordCount < 1000) {
    numCTAs = 1
  } else {
    numCTAs = 2
  }

  return { wordCount, numCTAs }
}

const parseFeaturedSnippet = (node, index, transform) => {
  const nodes = parseNodeChildren(node, index, transform, null, true)
  const styled = node.attribs?.class?.includes("featured-snippet-styled")

  return styled ? (
    <Flex
      key={`featured-snippet-${index}`}
      id="featured-snippet"
      padding="2rem"
      gap="2rem"
      margin="0 0 2rem"
      borderRadius="1rem"
      background={theme.color.indigo100}
    >
      {nodes.map((node, i) => {
        return <React.Fragment key={`snippet-item-${i}`}>{node}</React.Fragment>
      })}
    </Flex>
  ) : (
    <Flex gap="2rem" key={`featured-snippet-${index}`} id="featured-snippet">
      {nodes.map((node, i) => {
        return <React.Fragment key={`snippet-item-${i}`}>{node}</React.Fragment>
      })}
    </Flex>
  )
}

const pTagHasImageTag = node => {
  if (node.type === "tag" && node.name === "img") {
    return node
  }
  if (node.children?.[0]) return pTagHasImageTag(node.children[0])
  return false
}

const parseElement = (
  node,
  index,
  transform,
  parseElements,
  images,
  styles,
  inherit,
  ctaData
) => {
  if (
    ctaData &&
    node.type === "tag" &&
    ["h1", "h2", "h3", "h4", "h5", "h6"].includes(node.name)
  ) {
    ctaData.headingIndex++
    if (
      ctaData.headingIndex % ctaData.interval === 0 &&
      ctaData.headingIndex / ctaData.interval <= ctaData.numCTAs
    ) {
      return (
        <React.Fragment key={index}>
          <Div margin="2rem 0" hideOnMd>
            <WideColourfulCTA />
          </Div>
          {parseElements
            ? parseHeading(node, index, transform)
            : convertNodeToElement(node, index, transform)}
        </React.Fragment>
      )
    }
  }

  if (node.type === "tag") {
    switch (node.name) {
      case ["h1", "h2", "h3", "h4", "h5", "h6"].includes(node.name) && ctaData:
      case "img":
        return parseImage(node, images, index, false)
      case "ul":
      case "ol":
        if (parseElements) {
          return parseList(node, index, transform, styles, inherit)
        }
        break
      case "button":
        if (node.attribs?.class?.includes("new-accordion")) {
          return parseAccordion(node, index, transform)
        }
        break
      case "pre":
        if (node.attribs?.class?.includes("youtube-embed")) {
          return parseYouTubeEmbed(node, index, transform)
        } else if (node.attribs?.class?.includes("sign-up-cta")) {
          return parseSignUpCTA(node, index, images)
        } else if (node.attribs?.class?.includes("article-cta")) {
          return parseArticleCTA(index)
        } else if (node.attribs?.class?.includes("image-wrapper-shadow")) {
          return parseImageWrapper(node, index, images, true)
        } else if (node.attribs?.class?.includes("image-wrapper")) {
          return parseImageWrapper(node, index, images, false)
        }
        break
      case "div":
        if (node.attribs?.class?.includes("accordion-panel")) {
          return null
        } else if (node.attribs?.class?.includes("featured-snippet")) {
          return parseFeaturedSnippet(node, index, transform)
        } else if (node.attribs?.class?.includes("resource-example__grid")) {
          return parseExampleGrid(node, index, transform, images)
        } else if (node.attribs?.class?.includes("example-image-grid")) {
          return parseExampleImage(node, index, images)
        } else if (node.attribs?.class?.includes("dynamic-cta")) {
          return parseArticleCTA(index)
        }
        break
      case "p": {
        let imageNode = pTagHasImageTag(node)
        if (imageNode) {
          return parseImage(imageNode, images, index, false)
        } else if (parseElements) {
          return parseParagraph(node, index, transform, styles, inherit)
        }
        break
      }
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6":
        if (parseElements) {
          return parseHeading(node, index, transform, styles)
        }
        break
      case "a":
        if (parseElements) {
          return parseAnchor(node, index, transform, styles)
        }
        break
      case "blockquote":
        if (parseElements) {
          return parseQuote(node, index, transform)
        }
        break
      default:
        return convertNodeToElement(node, index, transform)
    }
  } else if (node.type === "text") {
    // This is original code from before. Does this even do anything? Doesn't seem like it
    const regex = /&#([0-9]+);/gm
    const single = /&#([0-9]+);/
    const matches = node.data.match(regex)
    if (matches) {
      for (const match of matches) {
        const num = match.match(single)[1]
        node.data = node.data.replace(match, String.fromCharCode(num))
      }
    }
  }

  return undefined
}

const BlogHtml = ({ html, inherit, styles, parseElements = false, images, cta }) => {
  let ctaData
  if (cta) {
    const { numCTAs } = getCtaCount(html)
    const countHeadings = html => {
      const headingRegex = /<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi
      const matches = html.match(headingRegex)
      return matches ? matches.length : 0
    }

    const headingsCount = countHeadings(html)

    ctaData = {
      headingIndex: 0,
      numCTAs: numCTAs,
      interval: Math.ceil(headingsCount / numCTAs),
    }
  }

  const transform = (node, index) =>
    parseElement(
      node,
      index,
      transform,
      parseElements,
      images,
      styles,
      inherit,
      ctaData
    )

  return ReactHtmlParser(html, { transform })
}

BlogHtml.propTypes = {
  html: PropTypes.string.isRequired,
  styles: HtmlStylesType
}

export default BlogHtml
